/**
 * Puerta de sincronización entre el arranque de Keycloak y las peticiones HTTP.
 *
 * El interceptor de axios necesita `keycloak.token`, pero `<AuthProvider>` no
 * llama a `keycloak.init()` de inmediato. Sin esta puerta, cualquier petición
 * lanzada en esa ventana saldría sin token.
 *
 * Vive en su propio módulo, y no en `keycloak.ts`, a propósito: decenas de
 * tests mockean `@/contexts/auth-provider/keycloak` con una factory que sólo
 * expone `getKeycloakInstance`, y `vi.mock` sustituye el módulo entero. Añadir
 * aquí estos exports los dejaría indefinidos en todos esos tests.
 *
 * Por defecto la puerta está ABIERTA: mientras nadie declare que el arranque
 * empezó, `whenKeycloakReady()` resuelve al instante. Así los tests que nunca
 * montan `<AuthProvider>` no se quedan colgados.
 */

let initStarted = false;
let settled = false;
let resolveReady: () => void = () => {};
let readyPromise: Promise<void> = new Promise<void>((resolve) => {
  resolveReady = resolve;
});

/**
 * Cierra la puerta. `<AuthProvider>` la llama en cuanto decide arrancar
 * Keycloak, antes de cualquier espera, para que ninguna petición se cuele.
 */
export const markKeycloakInitStarted = (): void => {
  initStarted = true;
};

/**
 * Abre la puerta. Debe llamarse cuando `init()` termina, **tanto si va bien
 * como si falla**: un init fallido que no abriera dejaría colgada para siempre
 * cualquier petición posterior.
 */
export const markKeycloakInitSettled = (): void => {
  settled = true;
  resolveReady();
};

/** Espera a que el arranque termine. Nunca rechaza. */
export const whenKeycloakReady = (): Promise<void> => {
  if (!initStarted || settled) return Promise.resolve();
  return readyPromise;
};

/** Devuelve la puerta a su estado inicial. Sólo para tests. */
export const resetKeycloakReady = (): void => {
  initStarted = false;
  settled = false;
  readyPromise = new Promise<void>((resolve) => {
    resolveReady = resolve;
  });
};
